
import {
  defineComponent,
  ref,
  onMounted,
  reactive,
  computed,
  onUnmounted,
} from "vue";

import { useTypedStore } from "@/store";
import { getScores, getPlays, plays, scores } from "@/store/scores";
import router from "@/router";
import Splide from "@splidejs/splide";
import Share from "@/components/Share.vue";
import ScoreAverageScaler from "@/components/ScoreAverageScaler.vue";
import CategoryBreakdown from "@/components/CategoryBreakdown.vue";
import ResultBlock from "@/components/ResultBlock.vue";
import InfoGrid from "@/components/InfoGrid.vue";
import { Answer, questions } from "@/data/questions";
import { diff, getPopularAnswers, isProfane } from "@/helpers";
import { getQuestionStats, stats } from "@/store/stats";
import { disposeCanvas, useCanvas } from "@/game-engine";
import { clearAnswers, processAnswer } from "@/game-engine/environment";

export default defineComponent({
  name: "Results",
  components: {
    share: Share,
    scoreAvg: ScoreAverageScaler,
    catScore: CategoryBreakdown,
    resultBlock: ResultBlock,
    infoGrid: InfoGrid,
  },
  setup() {
    const canvasRef = ref<HTMLCanvasElement | null>(null);
    const slideEle = ref<HTMLElement | null>(null);
    const resultsRef = ref<HTMLElement | null>(null);
    const rulesRef = ref<HTMLElement | null>(null);
    const { state, getters, dispatch, commit } = useTypedStore();
    const showRules = ref<boolean>(false);

    const isCommunityCity = ref(false);
    const communityCityButton = computed(() =>
      isCommunityCity.value ? "Your City" : "Community city",
    );
    const scrolled = ref(false);

    const maxScore = 79;
    const minScore = -79;
    const catMax = 18;
    const catMin = -18;

    const person = reactive({
      type: "Student",
      email: "",
      name: "",
    });

    const comment = reactive({
      name: "",
      organisation: "",
      comment: "",
    });

    const yourAverage = computed(() => {
      const onePerCent = (Math.abs(minScore) + Math.abs(maxScore)) / 100;
      const difference = diff(getters.score, mytopiaAverage.value);
      //const percent = difference * onePerCent;

      if (getters.score > mytopiaAverage.value) {
        //return `That's ${percent.toFixed(0)}% better than the average score!`;
        return `That's better than the community average score! Nicely done! `;
      } else if (getters.score < mytopiaAverage.value) {
        // return `Your score was ${percent.toFixed(0)}% lower than the average.`;
        return `That's lower than the community average score of ${mytopiaAverage.value}.`;
      } else {
        return `Your score was the same as the city average.`;
      }
    });

    const cityAverage = computed(() => {
      const onePerCent = (Math.abs(minScore) + Math.abs(maxScore)) / 100;
      const difference = diff(getters.score, mytopiaAverage.value);
      //const percent = difference * onePerCent;

      if (mytopiaAverage.value > getters.score) {
        // return `That's ${percent.toFixed(0)}% better than your score!`;
        return `That's better than your score of ${getters.score}.`;
      } else if (mytopiaAverage.value < getters.score) {
        // return `The community average was ${percent.toFixed(
        //   0
        // )}% lower than your score.`;
        return `You scored higher than the community average score! Nice work!`;
      } else {
        return `The community average is the same as your score.`;
      }
    });

    const toggleRules = () => {
      showRules.value = !showRules.value;
      console.log(rulesRef.value);
      if (rulesRef.value) {
        rulesRef.value.scrollIntoView({ behavior: "smooth" });
      }
    };

    const submittingScore = ref<boolean>(false);
    const scoreSubmittedSuccessfully = ref<boolean>(false);
    const scoreSubmittedPreviously = state.submittedScore;

    const submittingComment = ref<boolean>(false);
    const commentSubmittedSuccessfully = ref<boolean>(false);
    const commentSubmittedPreviously = state.submittedComment;
    const commentContainsProfanity = ref<boolean>(false);

    const canSwap = ref(false);
    const isLoaded = ref(false);
    const swap = () => {
      canSwap.value = false;
      if (!isCommunityCity.value) {
        clearAnswers();
        const answers = getPopularAnswers();
        if (answers) {
          buildCity(answers);
          canvasRef.value?.scrollIntoView();
        }
      } else {
        clearAnswers();
        if (getters.selectedAnswers) {
          buildCity(getters.selectedAnswers);
          canvasRef.value?.scrollIntoView();
        }
      }
      isCommunityCity.value = !isCommunityCity.value;
      setTimeout(() => {
        canSwap.value = true;
      }, 3000);
    };

    const resultAnswers = computed((): Answer[] => {
      return isCommunityCity.value
        ? getPopularAnswers()
        : getters.selectedAnswers;
    });

    const computedPlays = computed((): string => {
      return plays.value.toLocaleString("en-GB");
    });

    // const submitScore = async () => {
    //   submittingScore.value = true;

    //   commit("setSubmittedScore", true);

    //   const success: boolean = await dispatch("submitScore", {
    //     ...person,
    //     score: getters.score,
    //   });

    //   getScores();

    //   submittingScore.value = false;
    //   scoreSubmittedSuccessfully.value = success;
    // };

    // const submitComment = async () => {
    //   if (isProfane(comment.comment)) {
    //     commentContainsProfanity.value = true;
    //     return;
    //   }
    //   submittingComment.value = true;
    //   commentContainsProfanity.value = false;

    //   commit("setSubmittedComment", true);

    //   const success: boolean = await dispatch("submitComment", {
    //     ...comment,
    //   });
    //   getComments();
    //   submittingComment.value = false;
    //   commentSubmittedSuccessfully.value = success;
    // };

    const stars = computed((): number => {
      return Math.round(
        Math.max(0, Math.min(5, ((getters.score + 70) / 125) * 5)),
      );
    });

    const getNumberOfStars = (num: number): number => {
      return Math.round(Math.max(0, Math.min(5, (num + 10) / 4)));
    };

    const restartGame = () => {
      commit("resetState", undefined);
      disposeCanvas();
      router.push("/");
    };

    const mytopiaAverage = computed(() => {
      let added = 0;
      Object.values(scores.value).forEach((element) => {
        added = added + parseInt(element.score);
      });

      return Math.ceil(added / Object.values(scores.value).length);
    });

    const buildCity = (answers: Answer[]) => {
      answers.forEach((element, index) => {
        if (element) {
          processAnswer(
            element.modelPath,
            `question_${index + 1}/Build`,
            0,
            false,
            false,
          );
        }
      });
    };

    onMounted(async () => {
      window.addEventListener("scroll", () => {
        if (scrolled.value === false && window.scrollY > 0) {
          scrolled.value = true;
        }
      });
      // nextTick();
      if (getters.selectedAnswers.length < 6) {
        restartGame();
      }

      state.isResultsPage = true;
      await getScores();
      await getQuestionStats();
      if (canvasRef.value && getters.selectedAnswers) {
        useCanvas(canvasRef.value);
        setTimeout(() => {
          buildCity(getters.selectedAnswers);
        }, 1000);
        setTimeout(() => {
          canSwap.value = true;
          isLoaded.value = true;
        }, 3000);
      }

      //Audio.destroy();

      if (slideEle.value) {
        new Splide(slideEle.value, {
          padding: {
            right: "0.5rem",
            left: "0.5rem",
          },
          perPage: 3,
          breakpoints: {
            "800": {
              gap: "1rem",
              perPage: 1,
            },
            "1000": {
              perPage: 1,
            },
            "1400": {
              perPage: 2,
            },
          },
          pagination: false,
          gap: "2rem",
        }).mount();
      }

      getPlays();
    });

    onUnmounted(() => {
      window.removeEventListener("scroll", () => {
        if (scrolled.value === false && window.screenY > 0) {
          scrolled.value = true;
        }
      });
    });

    return {
      resultsRef,
      rulesRef,
      getters,
      state,
      person,
      submittingScore,
      scoreSubmittedPreviously,
      scoreSubmittedSuccessfully,
      submittingComment,
      commentSubmittedSuccessfully,
      commentSubmittedPreviously,
      commentContainsProfanity,
      restartGame,
      comment,
      computedPlays,
      showRules,
      yourAverage,
      questions,
      minScore,
      maxScore,
      catMin,
      catMax,
      mytopiaAverage,
      stats,
      isCommunityCity,
      cityAverage,
      communityCityButton,
      canvasRef,
      swap,
      canSwap,
      isLoaded,
      resultAnswers,
      scrolled,
      toggleRules,
    };
  },
});
